import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { List, Text, TextSkeleton } from "@src/appV2/redesign/system/ui-components";
import { useGetWorkplacesById } from "@src/appV2/redesign/Workplace/useGetWorkplacesById";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { minutesToMilliseconds } from "date-fns";

import { useCredentialRequirementConfiguration } from "../../useCredentialRequirementConfiguration";
import { getApplicableWorkplacesDetails } from "../../utils/getApplicableWorkplacesDetails";
import { getApplicableWorkplacesDisplayText } from "../../utils/getApplicableWorkplacesDisplayText";
import { getRequirementWorkplacesIds } from "../getRequirementWorkplacesIds";
import { useRequirementDetailsContext } from "../useRequirementDetailsContext";
import { DocumentDetailsRequiredByWorkplacesListItem } from "./ListItem";

export function DocumentDetailsRequiredBySection() {
  const { requirement: workerRequirement } = useRequirementDetailsContext();
  const { requirementId } = workerRequirement;
  const worker = useDefinedWorker();

  const requiredWorkplaceIds = getRequirementWorkplacesIds(workerRequirement);
  const applicableWorkplacesDetails = getApplicableWorkplacesDetails({
    workerRequirement,
    workerAddress: worker.address,
    requiredByWorkplacesCount: requiredWorkplaceIds.length,
  });

  const {
    data: workplaces,
    isLoading: isLoadingWorkplaces,
    fetchStatus: fetchStatusWorkplaces,
    isError: isErrorWorkplaces,
  } = useGetWorkplacesById(
    { workplaceIds: requiredWorkplaceIds },
    {
      enabled: isDefined(applicableWorkplacesDetails) && requiredWorkplaceIds.length === 1,
      staleTime: minutesToMilliseconds(5),
    }
  );

  const credentialRequirementConfiguration = useCredentialRequirementConfiguration();
  const isCredentialRequirement = credentialRequirementConfiguration.has(requirementId);

  const displayText = getApplicableWorkplacesDisplayText({
    applicableWorkplacesDetails,
    isSingleWorkplace: requiredWorkplaceIds.length === 1,
    workplaceName: workplaces?.pages?.[0]?.data?.[0]?.attributes?.name,
  });

  if (isLoadingWorkplaces && fetchStatusWorkplaces !== "idle") {
    return (
      <Stack justifyContent="center" minHeight={64}>
        <TextSkeleton variant="h4" width="60%" />
      </Stack>
    );
  }

  if (
    isCredentialRequirement ||
    !isDefined(displayText) ||
    !isDefined(applicableWorkplacesDetails) ||
    isErrorWorkplaces
  ) {
    return null;
  }

  return (
    <Stack spacing={1}>
      <Text semibold variant="h5" sx={(theme) => ({ color: theme.palette.text.secondary })}>
        Required by:
      </Text>

      <List dense>
        <DocumentDetailsRequiredByWorkplacesListItem
          text={displayText}
          type={applicableWorkplacesDetails.type}
          requiredWorkplaceIds={requiredWorkplaceIds}
          requirementId={requirementId}
        />
      </List>
    </Stack>
  );
}
